import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { List, Map } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FullScreenDialog, FullScreenDialogContent } from '@/components/ui/full-screen-dialog'
import { ArtistsList } from '@/components/artists/ArtistsList'
import { ArtistsMap } from '@/components/artists/ArtistsMap'
import {
  buildDiscoverLocationLabel,
  DiscoverFilterBar,
} from '@/components/discover/DiscoverFilterBar'
import type { DiscoverFilterDraft } from '@/components/discover/DiscoverFilterSheet'
import { FlashDetailContent } from '@/components/details/FlashDetailContent'
import { TattooDetailContent } from '@/components/details/TattooDetailContent'
import { ArtistSearchBar } from '@/components/shared/ArtistSearchBar'
import { Grid } from '@/components/shared/Grid'
import { TattooSearchBar } from '@/components/shared/TattooSearchBar'
import { isArtistEligibleForPublicListing, useArtistsData } from '@/contexts/ArtistsDataContext'
import { useArtistsCache } from '@/contexts/ArtistsCacheContext'
import { useFeedCache } from '@/contexts/FeedCacheContext'
import { useAppReady } from '@/contexts/AppReadyContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useArtistQuery } from '@/hooks/useArtistQuery'
import { useArtistSearch } from '@/hooks/useArtistSearch'
import { useAuthState } from '@/hooks/useAuth'
import { useCities } from '@/hooks/useCities'
import { useCollections } from '@/hooks/useCollections'
import { useDiscoverUrlFilters } from '@/hooks/useDiscoverUrlFilters'
import { useUserData } from '@/hooks/useUserData'
import { useVectorSearch } from '@/hooks/useVectorSearch'
import {
  areLocationSelectionsAllLaunched,
  buildAccordionRegions,
  isEffectivelyAllLocations,
  resolveSelectedLocationsToQueryCities,
  uniqueFlagCountryForLocationSelections,
  vectorSearchCityParamFromSelection,
} from '@/lib/locationRegions'
import { startDiscoverLoadSession } from '@/lib/discoverPerf'
import type { Artist } from '@/types/models'

export default function DiscoverPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { trackEvent } = useAnalytics()
  const { user, loading: authLoading } = useAuthState()
  const { userData, loading: userDataLoading } = useUserData()
  const { collections, loading: collectionsLoading } = useCollections()
  const { cities: launchedCities, cityCountryByCity, loading: launchedCitiesLoading } = useCities()
  const { availableArtists, cities: artistCatalogCities, loading: isLoadingArtistsData } =
    useArtistsData()
  const { signalContentReady, resetContentReady, reachMilestone } = useAppReady()
  const feedCache = useFeedCache()
  const artistsCache = useArtistsCache()

  const {
    discoverType,
    selectedLocations,
    tattooSorting,
    artistSorting,
    includeBooked,
    showDeals,
    showFollowingOnly,
    activeTab,
    allLocationsText,
    setDiscoverType,
    setSelectedLocations,
    setTattooSorting,
    setShowFollowingOnly,
    setActiveTab,
    applyDiscoverFilters,
  } = useDiscoverUrlFilters()

  const {
    searchResults: tattooSearchResults,
    isSearching: isTattooSearching,
    searchError: tattooSearchError,
    performSearch: performTattooSearch,
    clearSearch: clearTattooSearch,
    lastSearchRequest,
    searchCompletionId,
  } = useVectorSearch()

  const {
    searchResults: artistSearchResults,
    isSearching: isArtistSearching,
    performSearch: performArtistSearch,
    clearSearch: clearArtistSearch,
  } = useArtistSearch()

  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [detailType, setDetailType] = useState<'flash' | 'tattoo'>('flash')
  const [detailId, setDetailId] = useState('')
  const [isInTattooSearchMode, setIsInTattooSearchMode] = useState(false)
  const [hasActiveArtistSearch, setHasActiveArtistSearch] = useState(false)
  const [localArtistSearchResults, setLocalArtistSearchResults] = useState<Artist[]>([])
  const [hasInitializedSorting, setHasInitializedSorting] = useState(false)

  const isDiscoverRoute = location.pathname === '/discover'
  const isTattooType = discoverType === 'flashes' || discoverType === 'tattoos'
  const isArtistType = discoverType === 'artists'

  const locationEffectivelyAll = useMemo(() => {
    const catalog = isArtistType ? artistCatalogCities : launchedCities
    return isEffectivelyAllLocations(selectedLocations, allLocationsText, catalog)
  }, [selectedLocations, allLocationsText, launchedCities, artistCatalogCities, isArtistType])

  const locationAccordionRegions = useMemo(
    () => buildAccordionRegions(launchedCities, cityCountryByCity ?? {}),
    [launchedCities, cityCountryByCity],
  )

  const geoQueryCities = useMemo(
    () => resolveSelectedLocationsToQueryCities(selectedLocations, allLocationsText),
    [selectedLocations, allLocationsText],
  )

  const feedCacheKey = useMemo(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('flash')
    params.delete('tattoo')
    const qs = params.toString()
    return `discover:${qs}`
  }, [searchParams])

  const discoverLoadSessionStartedRef = useRef(false)
  if (
    location.pathname === '/discover' &&
    (discoverType === 'flashes' || discoverType === 'tattoos') &&
    !discoverLoadSessionStartedRef.current
  ) {
    discoverLoadSessionStartedRef.current = true
    startDiscoverLoadSession('discover_flashes_default', {
      discoverType,
      tattooSorting,
      cacheKey: feedCacheKey,
    })
  }

  const artistsCacheKey = useMemo(() => {
    const parts: string[] = [`type=artists`]
    const locKey = [...selectedLocations].sort().join(',')
    if (locKey) parts.push(`location=${locKey}`)
    if (artistSorting) parts.push(`sorting=${artistSorting}`)
    if (showFollowingOnly) parts.push('followingOnly=true')
    return `discover:${parts.join('&')}`
  }, [selectedLocations, artistSorting, showFollowingOnly])

  const {
    queryResults,
    discoverFallbackResults,
    isQuerying,
    performQuery,
    initialLoadComplete,
  } = useArtistQuery(artistsCacheKey)

  useEffect(() => {
    if (!isDiscoverRoute || !isTattooType) return
    return () => {
      feedCache.updateScrollPosition(feedCacheKey, window.scrollY)
    }
  }, [isDiscoverRoute, isTattooType, feedCacheKey, feedCache])

  useEffect(() => {
    if (!isDiscoverRoute || !isArtistType) return
    return () => {
      if (user) {
        artistsCache.updateScrollPosition(artistsCacheKey, window.scrollY)
      }
    }
  }, [isDiscoverRoute, isArtistType, artistsCacheKey, artistsCache, user])

  const lastRestoredScrollKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isDiscoverRoute) {
      lastRestoredScrollKeyRef.current = null
    }
  }, [isDiscoverRoute])

  useEffect(() => {
    if (!isDiscoverRoute) return
    const cacheKey = isTattooType ? feedCacheKey : artistsCacheKey
    if (lastRestoredScrollKeyRef.current === cacheKey) return
    const cache = isTattooType ? feedCache : artistsCache
    const cachedEntry = cache.getEntry(cacheKey)
    if (cachedEntry && cachedEntry.scrollPosition > 0) {
      lastRestoredScrollKeyRef.current = cacheKey
      const timer = setTimeout(() => {
        window.scrollTo({ top: cachedEntry.scrollPosition, behavior: 'instant' })
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isDiscoverRoute, isTattooType, feedCacheKey, artistsCacheKey, feedCache, artistsCache])

  useEffect(() => {
    reachMilestone('route_loaded')
  }, [reachMilestone])

  useEffect(() => {
    if (isTattooType) {
      signalContentReady()
    }
  }, [isTattooType, signalContentReady])

  useEffect(() => {
    const catalog = isArtistType ? artistCatalogCities : launchedCities
    const loading = isArtistType ? isLoadingArtistsData : launchedCitiesLoading
    if (loading || catalog.length === 0) return
    if (!selectedLocations.length || selectedLocations.includes(allLocationsText)) return
    const launched = new Set(catalog)
    if (!areLocationSelectionsAllLaunched(selectedLocations, allLocationsText, launched)) {
      setSelectedLocations([])
    }
  }, [
    isArtistType,
    isLoadingArtistsData,
    launchedCitiesLoading,
    launchedCities,
    artistCatalogCities,
    selectedLocations,
    allLocationsText,
    setSelectedLocations,
  ])

  const hasActiveTattooSearch =
    isInTattooSearchMode || tattooSearchResults.length > 0 || isTattooSearching

  const userQualifiesForFyp = useMemo(() => {
    if (!user || userDataLoading || collectionsLoading || !userData) return false
    const followedArtistsCount =
      userData?.following?.length || userData?.followingArtists?.length || 0
    const totalSavedItemsCount = collections.reduce(
      (total, collection) => total + collection.itemCount,
      0,
    )
    return followedArtistsCount >= 3 && totalSavedItemsCount >= 3
  }, [user, userData, collections, userDataLoading, collectionsLoading])

  useEffect(() => {
    const flashId = searchParams.get('flash')
    const tattooId = searchParams.get('tattoo')
    if (flashId) {
      setDetailType('flash')
      setDetailId(flashId)
      setDetailModalOpen(true)
    } else if (tattooId) {
      setDetailType('tattoo')
      setDetailId(tattooId)
      setDetailModalOpen(true)
    } else {
      setDetailModalOpen(false)
      setDetailId('')
    }
  }, [searchParams])

  const handleCloseDetailModal = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('flash')
    newParams.delete('tattoo')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  useEffect(() => {
    trackEvent(isArtistType ? 'artist_list_viewed' : 'feed_viewed', {
      feed_type: discoverType,
      tab: isArtistType ? activeTab : undefined,
    })
  }, [discoverType, isArtistType, activeTab, trackEvent])

  useEffect(() => {
    if (!isTattooType) return
    if (discoverType === 'tattoos' && (tattooSorting === 'priceAsc' || tattooSorting === 'priceDesc')) {
      setTattooSorting('recent')
    }
  }, [discoverType, tattooSorting, setTattooSorting, isTattooType])

  useEffect(() => {
    if (!isTattooType) return
    if (
      (tattooSorting === 'priceAsc' || tattooSorting === 'priceDesc') &&
      discoverType !== 'flashes'
    ) {
      setDiscoverType('flashes')
    }
  }, [tattooSorting, discoverType, setDiscoverType, isTattooType])

  useEffect(() => {
    if (!isTattooType || hasInitializedSorting || authLoading) return
    if (!user) {
      if (tattooSorting === 'fyp') setTattooSorting('recent')
      setHasInitializedSorting(true)
      return
    }
    if (userDataLoading || collectionsLoading || !userData) return
    if (!userQualifiesForFyp && tattooSorting === 'fyp') {
      setTattooSorting('recent')
    }
    setHasInitializedSorting(true)
  }, [
    isTattooType,
    authLoading,
    user,
    userDataLoading,
    collectionsLoading,
    userData,
    userQualifiesForFyp,
    hasInitializedSorting,
    tattooSorting,
    setTattooSorting,
  ])

  const selectedCollections = useMemo((): ('flashes' | 'portfolioItems')[] => {
    return discoverType === 'flashes' ? ['flashes'] : ['portfolioItems']
  }, [discoverType])

  const searchCityParam = locationEffectivelyAll
    ? ''
    : vectorSearchCityParamFromSelection(selectedLocations, allLocationsText)

  const handleTattooSearch = (params: {
    imageUrl?: string
    query?: string
    city: string
    collections: ('flashes' | 'portfolioItems')[]
  }) => {
    setIsInTattooSearchMode(true)
    performTattooSearch({
      ...params,
      city: searchCityParam,
    })
  }

  const handleClearTattooSearch = useCallback(() => {
    setIsInTattooSearchMode(false)
    clearTattooSearch()
  }, [clearTattooSearch])

  useEffect(() => {
    if (isTattooType) {
      resetContentReady()
    }
  }, [
    isTattooType,
    selectedLocations,
    discoverType,
    includeBooked,
    showDeals,
    tattooSorting,
    resetContentReady,
  ])

  const canChangeTattooSorting = !showDeals && !hasActiveTattooSearch
  const safeTattooSorting = tattooSorting === 'fyp' && !user ? 'recent' : tattooSorting
  const gridSorting = hasActiveTattooSearch ? 'recent' : safeTattooSorting

  const localFilteredArtists = useMemo(() => {
    let filtered = availableArtists.filter(isArtistEligibleForPublicListing)
    if (geoQueryCities?.length) {
      const allowed = new Set(geoQueryCities)
      filtered = filtered.filter((artist) => !!(artist.location?.city && allowed.has(artist.location.city)))
    }
    if (artistSorting === 'discover' || artistSorting === 'trending') {
      filtered.sort((a, b) => (b.followers?.length || 0) - (a.followers?.length || 0))
    } else if (artistSorting === 'newest') {
      filtered.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt || 0)
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt || 0)
        return bDate.getTime() - aDate.getTime()
      })
    }
    return filtered
  }, [availableArtists, geoQueryCities, artistSorting])

  const queryResultsEligible = useMemo(
    () => queryResults.filter(isArtistEligibleForPublicListing),
    [queryResults],
  )

  const discoverFallbackEligible = useMemo(
    () => discoverFallbackResults.filter(isArtistEligibleForPublicListing),
    [discoverFallbackResults],
  )

  const followedArtistCount = useMemo(() => {
    if (!userData) return 0
    const rows = userData.following
    if (Array.isArray(rows) && rows.length > 0) {
      return rows.filter((row) => typeof row?.artistId === 'string' && row.artistId.length > 0).length
    }
    const legacy = userData.followingArtists
    if (Array.isArray(legacy)) return legacy.length
    return 0
  }, [userData])

  const filteredArtists = user ? queryResultsEligible : localFilteredArtists

  useEffect(() => {
    if (!isArtistType || !user || userDataLoading) return
    if (followedArtistCount === 0 && showFollowingOnly) {
      setShowFollowingOnly(false)
    }
  }, [isArtistType, user, userDataLoading, followedArtistCount, showFollowingOnly, setShowFollowingOnly])

  useEffect(() => {
    if (!isArtistType || !user || isLoadingArtistsData || hasActiveArtistSearch) return
    performQuery({
      cities: geoQueryCities,
      sorting: artistSorting,
      limit: 500,
      followingOnly: showFollowingOnly,
      fetchDiscoverFallbackWhenEmpty: showFollowingOnly,
    })
  }, [
    isArtistType,
    user,
    geoQueryCities,
    artistSorting,
    isLoadingArtistsData,
    hasActiveArtistSearch,
    showFollowingOnly,
    performQuery,
  ])

  useEffect(() => {
    if (!isArtistType) return
    if (hasActiveArtistSearch) {
      setHasActiveArtistSearch(false)
      setLocalArtistSearchResults([])
      if (user) clearArtistSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArtistType, selectedLocations, artistSorting, showFollowingOnly])

  const handleArtistSearch = (query: string) => {
    setHasActiveArtistSearch(true)
    setActiveTab('list')
    const normalizedQuery = query.trim()
    if (normalizedQuery) {
      trackEvent('artist_search', {
        query: normalizedQuery,
        city: geoQueryCities?.join(',') || undefined,
      })
    }
    if (user) {
      performArtistSearch({ query, cities: geoQueryCities })
    } else {
      const searchLower = query.toLowerCase()
      const results = localFilteredArtists.filter((artist) => {
        return (
          artist.displayName?.toLowerCase().includes(searchLower) ||
          artist.firstName?.toLowerCase().includes(searchLower) ||
          artist.lastName?.toLowerCase().includes(searchLower) ||
          artist.location?.city?.toLowerCase().includes(searchLower) ||
          artist.styles?.some((style) => style.toLowerCase().includes(searchLower))
        )
      })
      setLocalArtistSearchResults(results)
    }
  }

  const handleClearArtistSearch = () => {
    setHasActiveArtistSearch(false)
    setLocalArtistSearchResults([])
    if (user) clearArtistSearch()
  }

  const searchResultsEligible = useMemo(
    () => artistSearchResults.filter(isArtistEligibleForPublicListing),
    [artistSearchResults],
  )

  const displayedArtists = hasActiveArtistSearch
    ? user
      ? searchResultsEligible
      : localArtistSearchResults
    : filteredArtists

  const mapDisplayedArtists = useMemo(() => {
    if (hasActiveArtistSearch) return displayedArtists
    if (!user || !showFollowingOnly) return filteredArtists
    if (queryResultsEligible.length > 0) return queryResultsEligible
    return discoverFallbackEligible
  }, [
    hasActiveArtistSearch,
    displayedArtists,
    user,
    showFollowingOnly,
    filteredArtists,
    queryResultsEligible,
    discoverFallbackEligible,
  ])

  const isLoadingArtists =
    isArtistSearching ||
    isQuerying ||
    (!user && !hasActiveArtistSearch && isLoadingArtistsData) ||
    (Boolean(user) && !hasActiveArtistSearch && !initialLoadComplete)

  const appliedFilters: DiscoverFilterDraft = {
    discoverType,
    selectedLocations,
    tattooSorting,
    artistSorting,
    includeBooked,
    showDeals,
    showFollowingOnly,
    activeTab,
  }

  const handleApplyFilters = (draft: DiscoverFilterDraft) => {
    applyDiscoverFilters({
      discoverType: draft.discoverType,
      selectedLocations: draft.selectedLocations,
      tattooSorting: draft.tattooSorting,
      artistSorting: draft.artistSorting,
      includeBooked: draft.includeBooked,
      showDeals: draft.showDeals,
      showFollowingOnly: draft.showFollowingOnly,
      activeTab: draft.activeTab,
    })
  }

  const typeOptions = [
    { value: 'flashes', label: t('navigation.flashes', 'Flashes') },
    { value: 'tattoos', label: t('navigation.tattoos', 'Tattoos') },
    { value: 'artists', label: t('navigation.artists', 'Artists') },
  ] as const

  const typeLabel =
    typeOptions.find((option) => option.value === discoverType)?.label ??
    t('navigation.flashes', 'Flashes')

  const tattooSortOptions = [
    { value: 'trending', label: t('filters.sorting.trending', 'Trending') },
    { value: 'recent', label: t('filters.sorting.recent', 'Newest') },
    { value: 'fyp', label: t('filters.sorting.fyp', 'For You') },
    { value: 'priceAsc', label: t('filters.sorting.priceAsc', 'Price: Low to High') },
    { value: 'priceDesc', label: t('filters.sorting.priceDesc', 'Price: High to Low') },
  ] as const

  const artistSortOptions = [
    { value: 'discover', label: t('filters.sorting.discover', 'Discover') },
    { value: 'trending', label: t('filters.sorting.trending', 'Trending') },
    { value: 'newest', label: t('filters.sorting.recent', 'Newest') },
  ] as const

  const sortLabel = isArtistType
    ? artistSortOptions.find((option) => option.value === artistSorting)?.label ??
      t('filters.sorting.discover', 'Discover')
    : canChangeTattooSorting
      ? tattooSortOptions.find((option) => option.value === safeTattooSorting)?.label ??
        t('filters.sorting.recent', 'Newest')
      : t('filters.sorting.search', 'Search')

  const locationLabel = buildDiscoverLocationLabel(
    selectedLocations,
    allLocationsText,
    locationAccordionRegions,
    locationEffectivelyAll,
  )

  const locationFlagCountry = uniqueFlagCountryForLocationSelections(
    selectedLocations,
    allLocationsText,
    cityCountryByCity ?? {},
  )

  const toggleMapList = () => {
    const next: 'list' | 'map' = activeTab === 'map' ? 'list' : 'map'
    setActiveTab(next)
    trackEvent('artist_tab_clicked', { tab_key: next, page: 'discover_page' })
  }

  const lastHandledSearchCompletionRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isTattooType || searchCompletionId === 0) return
    if (lastHandledSearchCompletionRef.current === searchCompletionId) return
    lastHandledSearchCompletionRef.current = searchCompletionId
    if (tattooSearchResults.length > 0 || !tattooSearchError || !lastSearchRequest) return
    void trackEvent('feed_search_empty', {
      feed_type: discoverType,
      query: lastSearchRequest.query?.trim() || undefined,
      has_image: Boolean(lastSearchRequest.imageUrl),
      city: lastSearchRequest.city || undefined,
      empty_reason: tattooSearchError === 'No results found' ? 'no_results' : 'search_failed',
      failure_message: tattooSearchError === 'No results found' ? undefined : tattooSearchError,
    })
  }, [
    isTattooType,
    searchCompletionId,
    tattooSearchResults.length,
    tattooSearchError,
    lastSearchRequest,
    discoverType,
    trackEvent,
  ])

  const discoverHeaderClassName =
    'sticky-header-notch-backdrop sticky top-0 shrink-0 md:top-[52px] z-40 bg-background/95 backdrop-blur-md md:border-b md:border-border/50 md:dark:border-border/30 md:rounded-b-2xl'

  const headerSection = (
    <div className={discoverHeaderClassName}>
      <div className="phone-container-padded space-y-3 pt-0 pb-3">
        {isArtistType ? (
          <ArtistSearchBar
            onSearch={handleArtistSearch}
            onClear={handleClearArtistSearch}
            isSearching={isArtistSearching}
            hasActiveSearch={hasActiveArtistSearch}
            startSlot={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="m-0 h-11 w-11 shrink-0 rounded-lg border-0 bg-secondary p-0 text-secondary-foreground transition-colors hover:bg-secondary/80"
                onClick={toggleMapList}
                aria-label={
                  activeTab === 'map'
                    ? t('artists.showList', { defaultValue: 'Show list' })
                    : t('artists.showMap', { defaultValue: 'Show map' })
                }
              >
                {activeTab === 'map' ? <List className="h-4 w-4" /> : <Map className="h-4 w-4" />}
              </Button>
            }
          />
        ) : (
          <TattooSearchBar
            onSearch={handleTattooSearch}
            onClear={handleClearTattooSearch}
            selectedCity={locationLabel}
            collections={selectedCollections}
            isSearching={isTattooSearching}
            hasActiveSearch={hasActiveTattooSearch}
          />
        )}

        <DiscoverFilterBar
          appliedFilters={appliedFilters}
          onApplyFilters={handleApplyFilters}
          typeLabel={typeLabel}
          sortLabel={sortLabel}
          locationLabel={locationLabel}
          locationEffectivelyAll={locationEffectivelyAll}
          locationFlagCountry={locationFlagCountry}
          showFypOption={userQualifiesForFyp}
          showFollowingOption={Boolean(user && followedArtistCount > 0)}
          canChangeTattooSorting={canChangeTattooSorting}
        />
      </div>
    </div>
  )

  const tattooContent = (
    <div className="relative z-0 phone-container-padded pb-20">
      <Grid
        key={`${selectedLocations.join(',')}-${discoverType}-${includeBooked}-${showDeals}-${gridSorting}`}
        selectedCollections={selectedCollections}
        selectedStyle={t('onskin.filters.allStyles')}
        selectedArtist={t('onskin.filters.allArtists')}
        selectedLocations={selectedLocations}
        sorting={gridSorting}
        includeBooked={includeBooked}
        showDeals={hasActiveTattooSearch ? false : showDeals}
        searchResults={
          tattooSearchResults.length > 0 && !tattooSearchError ? tattooSearchResults : undefined
        }
        isSearching={isTattooSearching}
        searchError={tattooSearchError}
        cacheKey={hasActiveTattooSearch ? undefined : feedCacheKey}
        locationFilterCoversAllMarkets={locationEffectivelyAll}
      />
    </div>
  )

  const artistsListContent =
    user && showFollowingOnly && !hasActiveArtistSearch ? (
      <div className="space-y-4">
        {queryResultsEligible.length > 0 ? (
          <ArtistsList
            artists={queryResultsEligible}
            showCountryFlags={locationEffectivelyAll}
            isLoading={isLoadingArtists}
          />
        ) : isLoadingArtists || !initialLoadComplete ? (
          <ArtistsList artists={[]} showCountryFlags={locationEffectivelyAll} isLoading />
        ) : (
          <>
            <div className="space-y-2 py-3 text-left">
              <p className="text-base font-medium leading-snug text-muted-foreground">
                {followedArtistCount === 0
                  ? t('artists.followingNobodyTitle')
                  : t('artists.followingFilteredTitle')}
              </p>
              <p className="whitespace-pre-line text-base font-medium leading-snug text-muted-foreground">
                {followedArtistCount === 0
                  ? t('artists.followingNobodyHint')
                  : t('artists.followingFilteredHint')}
              </p>
              {discoverFallbackEligible.length === 0 ? (
                <p className="text-base font-medium leading-snug text-muted-foreground">
                  {t('artists.discoverAlsoEmpty')}
                </p>
              ) : null}
            </div>
            {discoverFallbackEligible.length > 0 ? (
              <>
                <div className="py-3 text-left">
                  <p className="text-base font-medium leading-snug text-muted-foreground">
                    {t('artists.discoverSectionTitle')}
                  </p>
                </div>
                <ArtistsList
                  artists={discoverFallbackEligible}
                  showCountryFlags={locationEffectivelyAll}
                  isLoading={false}
                />
              </>
            ) : null}
          </>
        )}
      </div>
    ) : (
      <ArtistsList
        artists={displayedArtists}
        showCountryFlags={locationEffectivelyAll}
        isLoading={hasActiveArtistSearch ? isArtistSearching : isLoadingArtists}
      />
    )

  if (isArtistType && activeTab === 'map') {
    return (
      <div className="h-screen-mobile-minus-nav flex min-h-0 w-full max-w-full min-w-0 flex-col overflow-hidden overscroll-none bg-background transition-colors">
        {headerSection}
        <div className="flex min-h-0 flex-1 flex-col px-[var(--min-side-padding)]">
          <div className="flex min-h-0 flex-1 flex-col">
            <ArtistsMap artists={mapDisplayedArtists} isLoading={isLoadingArtists} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background transition-colors pb-bottom-nav">
        {headerSection}
        {isTattooType ? tattooContent : (
          <div className="relative z-0 phone-container-padded pb-20">{artistsListContent}</div>
        )}
      </div>

      <FullScreenDialog open={detailModalOpen} onOpenChange={handleCloseDetailModal}>
        <FullScreenDialogContent>
          {detailType === 'flash' && detailId ? (
            <FlashDetailContent
              flashId={detailId}
              onBack={handleCloseDetailModal}
              shareUrl={`${window.location.origin}/flashes/${detailId}`}
            />
          ) : null}
          {detailType === 'tattoo' && detailId ? (
            <TattooDetailContent
              tattooId={detailId}
              onBack={handleCloseDetailModal}
              shareUrl={`${window.location.origin}/tattoos/${detailId}`}
            />
          ) : null}
        </FullScreenDialogContent>
      </FullScreenDialog>
    </>
  )
}
